<template>
  <div class="bg-picker">
    <div class="relative bg-main border-b border-border">
      <div
        v-if="siteUsps.length > 0"
        class="text-size-chart text-center relative bg-white text-muted flex md:justify-center justify-around items-center h-32 z-header border-b"
      >
        <div
          v-for="usp in siteUsps"
          :key="'site-usp-' + usp.usp"
          class="md:mr-18 md:last:mr-0"
        >
          <fa class="text-xs mr-6" :icon="['fal', 'check']" />
          {{ usp.usp }}
        </div>
      </div>
      <div class="container xxl:px-24 h-full flex items-center bg-main">
        <div class="w-2/5 h-full flex items-center">
          <div
            class="cursor-pointer select-none py-16 flex items-center text-primary text-base-medium"
            @click="$router.back()"
          >
            <img src="/icons/back-blue.png" class="h-24 w-24 md:mr-8">
            <div v-if="!uiStore.isMobile">
              {{ $lang('checkout', 'header_back_btn') }}
            </div>
          </div>
        </div>
        <div class="w-2/5 flex justify-center">
          <nuxt-link :to="config.home_url">
            <img
              src="/images/header-logo.png"
              class="h-24 w-auto"
              alt="Ullmax-logga"
              height="24"
              width="102"
            >
          </nuxt-link>
        </div>
        <div class="w-2/5 flex justify-end">
          <div
            class="cursor-pointer select-none py-16 flex items-center text-primary text-base-medium"
            @click="openHelpMenu(true)"
          >
            <img
              src="/icons/help-blue.png"
              class="h-24 w-24 md:mr-8"
              alt="help"
            >
            <div v-if="!uiStore.isMobile">
              {{ $lang('checkout', 'header_help_btn') }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="container xxl:px-24 md:px-56 px-0 min-h-screen">
      <div
        v-if="items.length > 0"
        class="checkout md:py-56 py-24 flex md:flex-row-reverse flex-col"
      >
        <!-- * * * * cart (right column desktop) -->
        <div class="md:w-2/5">
          <div class="section mt-24 first:mt-0">
            <div class="px-24">
              <div class="pt-32 text-heading-small">
                {{ `${$lang('checkout', 'products')} (${cartStore.cartData.totalItemQuantity})` }}
              </div>
              <div>
                <CheckoutProductItem
                  v-for="item in items"
                  :key="item.item"
                  :item="item"
                  class="py-24"
                  is-checkout
                  @klarnasuspend="suspendKco"
                  @klarnaresume="resumeKco"
                />
              </div>
              <div
                v-if="hasFreeShippingBlock && !isSeller"
                class="flex justify-center items-center py-8 rounded-regular bg-orange-25 text-small mb-16 text-black-full"
                :class="{
                  'bg-green-75 text-white': freeShippingCount <= 0,
                }"
              >
                {{
                  freeShippingCount > 0
                    ? `${formatPriceUnit(freeShippingCount, country)} ${$lang(
                      'checkout',
                      'free_shipping_counter'
                    )}`
                    : $lang('checkout', 'free_shipping_completed')
                }}
              </div>
              <div v-if="!cartStore.cartData.isGiftCardCheckout && !isSeller" class="border-t">
                <div
                  class="flex justify-between text-primary select-none cursor-pointer py-16"
                  @click="voucherMenuIsToggled = !voucherMenuIsToggled"
                >
                  <div>
                    {{ $lang('checkout', 'add_discount_btn') }}
                  </div>
                  <fa :icon="['far', 'chevron-right']" />
                </div>
              </div>
              <div
                v-if="
                  !cartStore.cartData.isGiftCardCheckout && cartStore.cartData.vouchers.length > 0
                "
                class="border-t"
              >
                <div class="flex py-16 flex-wrap">
                  <div
                    v-for="voucher in cartStore.cartData.vouchers"
                    :key="'checkout-voucher-' + voucher.voucher"
                    class="mb-8 last:mb-0 flex flex-1 justify-between items-center"
                  >
                    <div
                      class="rounded-3xl py-8 pl-16 pr-32 bg-secondary text-primary text-price flex items-center relative"
                    >
                      {{ voucher.voucher }}
                      <div
                        class="absolute right-16 py-8 pl-8 cursor-pointer select-none"
                        @click="deleteVoucher(voucher.voucher)"
                      >
                        <fa class="text-sm" :icon="['far', 'times']" />
                      </div>
                    </div>
                    <div
                      class="text-warning"
                      :class="{ 'text-grey': isLoadingPrice }"
                    >
                      {{ voucher.priceOff }}
                    </div>
                  </div>
                </div>
              </div>
              <div class="pt-16 border-t">
                <div class="flex justify-between">
                  <div class="text-small">
                    {{ $lang('checkout', 'shipping_cost') }}:
                  </div>
                  <div
                    class="text-base-medium"
                    :class="{ 'text-muted': isLoadingPrice }"
                  >
                    {{ cartStore.cartData.shippingCost }}
                  </div>
                </div>
                <div class="flex justify-between">
                  <div class="text-small">
                    {{ $lang('checkout', 'products_sum') }}:
                  </div>
                  <div
                    class="text-base-medium"
                    :class="{ 'text-muted': isLoadingPrice }"
                  >
                    <span
                      :class="{
                        'text-warning': cartStore.cartData.hasDiscount,
                      }"
                    >
                      {{ cartStore.cartData.totalCost }}
                    </span>
                    <span
                      v-if="cartStore.cartData.hasDiscount"
                      class="line-through text-black-50 ml-8"
                    >
                      {{ cartStore.cartData.totalItemsCost }}
                    </span>
                  </div>
                </div>
                <div class="flex justify-between">
                  <div class="text-small">
                    {{ $lang('checkout', 'products_vat') }}:
                  </div>
                  <div
                    class="text-small"
                    :class="{ 'text-muted': isLoadingPrice }"
                  >
                    {{ cartStore.cartData.totalVAT }}
                  </div>
                </div>

                <div
                  v-if="!cartStore.cartData.isGiftCardCheckout"
                  class="mt-16 link-underline-wrapper"
                >
                  <label>
                    <input
                      id="newsletter"
                      v-model="newsletterChecked"
                      type="checkbox"
                      checked="checked"
                      class="inline-block w-auto mr-8"
                    >
                    <span v-html="page.newsletter_signup.title" />
                  </label>

                  <span
                    class="text-sm text-grey"
                    v-html="page.newsletter_signup.content"
                  />
                </div>
              </div>
            </div>
          </div>
          <!-- * * * * UPSELL -->
          <div
            v-if="!cartStore.cartData.isGiftCardCheckout && crossSellProducts.length"
            class="section mt-24 px-24 pt-32 overflow-hidden"
          >
            <div class="text-heading-small mb-16">
              {{ crossSellTitle }}
            </div>
            <div
              v-if="uiStore.isMobile"
              class="relative w-full"
            >
              <Swiper :slides-per-view="1.2">
                <SwiperSlide
                  v-for="crossSellProduct in crossSellProducts"
                  :key="'checkout-cross-sell-' + crossSellProduct.ecomId"
                >
                  <CheckoutUpsellItem
                    :item="crossSellProduct"
                    @klarnasuspend="suspendKco"
                    @klarnaresume="resumeKco"
                  />
                </SwiperSlide>
              </Swiper>
            </div>
            <div v-else>
              <div
                v-for="crossSellProduct in crossSellProducts"
                :key="'checkout-cross-sell-' + crossSellProduct.ecomId"
                class="mb-24 last:mb-24"
              >
                <CheckoutUpsellItem
                  :item="crossSellProduct"
                  @suspend-kco="suspendKco"
                  @resume-kco="resumeKco"
                />
              </div>
            </div>
          </div>
          <!-- * * * * /UPSELL -->
        </div>
        <!-- * * * * /cart -->

        <div class="md:mr-24 md:w-3/5">
          <!-- * * * * Club info -->
          <div
            v-if="clubCookie && sellerCookie && isSeller"
            class="section px-24 pt-32"
            v-html="sellerText"
          />
          <div v-else-if="isSeller" class="section px-24 pt-32">
            {{ $lang('checkout', 'choose_seller') }}
          </div>

          <!-- * * * * DELIVERY TYPE -->
          <div v-if="sellerCookie && isSeller && !invoiceRejected" class="section px-24 pt-32 mb-32">
            <div class="mb-18">{{ $lang('checkout', 'choose_delivery') }}</div>
            <div class=" grid grid-cols-2 gap-18">
              <CheckboxComponent
                :highlight="deliveryType === 'seller'"
                :is-radio="true"
                :checked="deliveryType === 'seller'"
                :text="$lang('checkout', 'seller_delivery')"
                @on-check="changeDeliveryType('seller')"
              />
              <CheckboxComponent
                :highlight="deliveryType === 'home'"
                :is-radio="true"
                :checked="deliveryType === 'home'"
                :text="$lang('checkout', 'home_delivery')"
                @on-check="changeDeliveryType('home')"
              />
              <div class="text-sm">
                {{ $lang('checkout', 'seller_delivery_explanation') }}
                <p v-if="!clubCookie.NoDeadlineDate && clubCookie.DeadlineDate">{{ $lang('checkout', 'seller_delivery_deadlinedate') }}: {{ clubCookie.DeadlineDate.replace('-', '&#8209;').substring(0, 10) }}</p>
              </div>
              <div class="text-sm">{{ $lang('checkout', 'home_delivery_explanation') }}</div>
            </div>
          </div>

          <!-- * * * * Delivery to seller -->
          <div v-if="deliveryType === 'seller'" class="section px-24 pt-32">
            <ExternalCheckout type="seller" />
          </div>

          <!-- * * * * UNIFAUN BOX -->
          <div
            v-if="!cartStore.cartData.isGiftCardCheckout && deliveryType === 'home' && allowSale"
            class="section px-24 pt-32"
          >
            <div class="text-heading-small mb-12">
              {{ $lang('checkout', 'delivery_heading') }}
            </div>
            <div v-if="unifaunInitiated" class="mb-12">
              <div class="text-small mb-8">
                {{ $lang('checkout', 'delivery_text') + ' ' + postalNumber }}
              </div>
              <div
                class="text-small underline text-primary mb-12 select-none cursor-pointer"
                @click="clearPostalNumber"
              >
                {{ $lang('checkout', 'delivery_change_postal') }}
              </div>
            </div>
            <div>
              <div v-show="unifaunInitiated" ref="deliveryDiv" />
              <div v-if="!unifaunInitiated" class="md:max-w-280">
                <TextInput
                  :init-value="initPostalNumber"
                  :placeholder="$lang('checkout', 'delivery_options_input_placeholder')"
                  autocomplete="postal-code"
                  :has-error="wrongPostalNo"
                  @submit-input="(pn) => postalNumber = pn"
                />
                <ButtonComponent
                  class="mt-12"
                  :loading="deliveryLoading"
                  :text="$lang('checkout', 'delivery_options_submit')"
                  is-block
                  @clicked="setupDeliveryOptions"
                />
              </div>
            </div>
          </div>
          <!-- * * * * PAYMENT BOX -->
          <div
            v-if="deliveryType === 'home' && allowSale"
            class="section"
            :class="{
              'mt-24': !cartStore.cartData.isGiftCardCheckout,
            }"
          >
            <div v-if="cartStore.cartData.isGiftCardCheckout">
              <ExternalCheckout type="paypal" />
            </div>
            <div v-else-if="unifaunInitiated">
              <div
                ref="el"
                class="sm:px-40 px-24 pt-16"
                :class="{
                  'pointer-events-none opacity-25': !unifaunWidgetValid,
                }"
              />
            </div>
            <div v-else class="px-24 pt-32">
              <div class="text-heading-small mb-12">
                {{ $lang('checkout', 'payment_locked_header') }}
              </div>
              <div class="text-small">
                {{ $lang('checkout', 'payment_locked_text') }}
              </div>
            </div>
          </div>
          <VoucherInputMenu
            v-if="voucherMenuIsToggled"
            @on-voucher-menu-close="voucherMenuIsToggled = false"
            @suspend-kco="suspendKco"
            @resume-kco="resumeKco"
            @voucher-applied="voucherApplied"
          />
          <InformationMenu
            v-if="helpMenuOpen"
            :title="informationTitle"
            :content="informationContent"
            @on-help-menu-close="closeHelpMenu"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useCartStore } from '~/store/cart';
import { useConfigStore } from '~/store/config';
import CartItem from '~/models/cart-item';
import type { CartItemType } from '~/constants/types/cart-item';
import type { ShippingMethod } from '~/constants/types/shipping-method';
import CheckoutPage, { type PageType } from '~/models/checkout-page';
import { useUiStore } from '~/store/ui';
import CheckoutProductItem from '~/components/cart/CheckoutProductItem.vue';
import { formatPriceUnit } from '~/util/price-formatting';
import ButtonComponent from '~/components/forms/ButtonComponent.vue';
import TextInput from '~/components/forms/TextInput.vue';
import InformationMenu from '~/components/checkout/InformationMenu.vue';
import VoucherInputMenu from '~/components/checkout/VoucherInputMenu.vue';
import type { CartType, CentraPaymentResponse } from '~/constants/types/cart';
import type {
  OrderAdditionalDataInterface,
  ShippingOptionsInterface
} from '~/constants/interfaces/order-additional-data';
import type { UnifaunSetDelivery } from '~/constants/types/unifaun';
import Product from '~/models/product';
import CheckoutUpsellItem from '~/components/checkout/CheckoutUpsellItem.vue';
import ExternalCheckout from '~/components/checkout/ExternalCheckout.vue';
import type { SellerClub, SellerPerson } from '~/constants/types/seller';
import CheckboxComponent from '~/components/forms/CheckboxComponent.vue';
import type { EcomProduct } from '~/constants/types/product';

const cartStore = useCartStore();
const config = useConfigStore();
const runtimeConfig = useRuntimeConfig();
const uiStore = useUiStore();
const { centraPost, centraDelete, unifaunGet, centraPut } = useApiFetch();
const { $lang } = useNuxtApp();
const route = useRoute();
const { fetchProducts } = useProductService();
const isLoadingPrice = ref(false);
const selectedShipping = ref('');
const newsletterChecked = ref(true);
const voucherMenuIsToggled = ref(false);
const crossSellProducts = ref([]);
const helpMenuOpen = ref(false);
const informationTitle = ref('');
const informationContent = ref('');
const unifaunInitiated = ref(false);
const postalNumber = ref('');
const deliveryLoading = ref(false);
const unifaunWidget = ref<any>(null);
const unifaunWidgetValid = ref(false);
const orderAdditionalData = useCookie<OrderAdditionalDataInterface>('orderAdditionalData', { maxAge: 3600 * 24 * 182, path: '/' });

// Seller
const clubCookie = useCookie<SellerClub>('club', { maxAge: 3600 * 24 * 182, path: '/' });
const sellerCookie = useCookie<SellerPerson>('seller', { maxAge: 3600 * 24 * 182, path: '/' });
const isSeller = useCookie<boolean>('isSeller', { maxAge: 3600 * 24 * 182, path: '/' });
const invoiceRejected = useCookie<boolean>('invoiceRejected', { maxAge: 3600 * 24 * 182, path: '/', default: () => false });

const props = defineProps<{
  page: PageType,
}>();

const items = computed<CartItem[]>(()=> {
  const items = cartStore.cartData.items || [];
  return items.map(
    (item: CartItemType) => new CartItem({ cartItem: item })
  );
});

onMounted(()=> {
  additionalNotes();
  getCrossSellProducts();
  sendGtmCheckoutStep();

  if (isSeller.value && !sellerCookie.value) {
    uiStore.sellerDialogSettings = {
      sellerMissing: true,
      forceSellerSelection: true,
    };
    uiStore.showSellerDialog = true;
  }
});

/**
 * Checks if either the visitor came without a seller link, or seller cookie is set
 */
const allowSale = computed(()=> {
  return (!isSeller.value || (isSeller.value && sellerCookie.value));
});

watch(()=> cartStore.cartData.shippingAddress?.zipCode, (newVal) => {
  postalNumber.value = newVal;
});

const country = computed<string>(()=> {
  return config?.market?.country || '';
});

// * * * * * * * * * * * * * * * * * * * FREE SHIPPING
const wpSelectedShippingMethod = computed<ShippingMethod | null>(()=> {
  const wpShippingMethod = config.shippingMethods.find(
    (method) => selectedShipping.value === method.centra_shipping_method
  );
  return wpShippingMethod || null;
});

const freeShippingCount = computed<number>(()=> {
  if (wpSelectedShippingMethod.value) {
    const freeShippingLimit = Number(
      wpSelectedShippingMethod.value.free_shipping_limit
    );
    return freeShippingLimit - cartStore.cartData.totalItemsCostAsNumber || 0;
  } else {
    return 0;
  }
});

const freeShippingLimit = computed<number>(()=> {
  if (wpSelectedShippingMethod.value) {
    return Number(
      wpSelectedShippingMethod.value.free_shipping_limit
    );
  } else {
    return 600;
  }
});

const hasFreeShipping = computed<boolean>(()=> {
  return cartStore.cartData.shippingCostAsNumber <= 0;
});

const hasFreeShippingBlock = computed<boolean>(()=> {
  if (deliveryType.value === 'seller') {
    return false;
  }
  if (wpSelectedShippingMethod.value) {
    return (
      wpSelectedShippingMethod.value.has_free_shipping_limit
    );
  } else {
    return false;
  }
});

const checkoutPageDocument = computed<CheckoutPage>(()=> {
  return new CheckoutPage({ contentData: props.page });
});

const crossSellTitle = computed<string>(()=> {
  return checkoutPageDocument.value.crossSell ? checkoutPageDocument.value.crossSell.title : '';
});

const siteUsps = computed<{usp: string}[]>(()=> {
  return config.usps || [];
});

const initPostalNumber = computed<string>(()=> {
  return cartStore?.cartData?.shippingAddress?.zipCode || '';
});

// * * * * * * * * * * * * * * * * * * * DELIVERY
type DeliveryTypes = 'home' | 'seller';
const deliveryType = ref<DeliveryTypes>(invoiceRejected.value ? 'home': 'seller');
if (!isSeller.value) {
  deliveryType.value = 'home';
}

onMounted(()=> {
  if (deliveryType.value === 'seller') {
    changeDeliveryType('seller');
  }
});

/**
 * Changes between standard delivery and delivery to seller
 */
const changeDeliveryType = async(to: DeliveryTypes) => {
  deliveryType.value = to;
  unifaunInitiated.value = false;
  if (to === 'seller') {
    const res = await centraPut('/shipping-methods/seller-delivery');
    if (res) {
      cartStore._cartData = res;
    }
  } else {
    //let defaultshippingMethod = config.shippingMethods[0].centra_shipping_method;
    //const res = await centraPut('/shipping-methods/' + defaultshippingMethod);  
    const res = await centraPut('/shipping-methods/seller-homedelivery/');
    if (res) {
      cartStore._cartData = res;
    }
  }
};

const unifaunWidgetUrl = computed(() => {
  const freeShipping = (cartStore.cartData.shippingCostAsNumber <= 0);
  const allowFreeShipping = isSeller.value && deliveryType.value === 'home' ? false : true;
  return '/api/unifaun/delivery-checkouts/' +
    runtimeConfig.public.unifaunId +
    '?currency=' + cartStore.cartData.currency +
    '&tocountry=' + config.market.country +
    '&tozipcode=' + postalNumber.value +
    '&language=' + config.market.language +
    '&cartprice=' + cartStore.cartData.totalItemsCostAsNumber +
    '&freeshippinglimit=' + freeShippingLimit.value +
    '&freeshipping=' + freeShipping +
    '&allowfreeshipping=' + allowFreeShipping +
    '&sellerlink=' + isSeller.value +
    '&onlypickup=false';
});

const sellerText = computed(()=> {
  let sellerText = $lang('checkout', 'profit_club_seller', {
    club: '<span class="font-medium">' + clubCookie.value.Name +'</span>',
    seller: '<span class="font-medium">' + sellerCookie.value.Name +'</span>',
  });
  if (invoiceRejected.value) {
    sellerText += '<p class="font-medium">' + $lang('checkout', 'seller_rejected_info')+'</p>';
  }
  return sellerText;
});

const deliveryDiv = ref<HTMLDivElement | null>(null);

const wrongPostalNo = ref(false);

watch(postalNumber, ()=> {
  wrongPostalNo.value = false;
});

const setupDeliveryOptions = async() => {
  console.log(postalNumber.value);
  if (!/^\d{4}$|^\d{3}\s\d{2}$|^\d{5}$/.test(postalNumber.value)) {
    wrongPostalNo.value = true;
    return;
  }
  wrongPostalNo.value = false;

  deliveryLoading.value = true;

  const res = await unifaunGet(unifaunWidgetUrl.value);

  if (res && window.UnifaunCheckout) {
    unifaunWidget.value = window.UnifaunCheckout.createAt(deliveryDiv.value, {
      language: config.market?.language ?? 'sv',
      useIcons: true,
      narrowDisplay: true,
      resultCallback: (res: UnifaunSetDelivery) => {
        onSelectShippingMethod(res);
      },
      iconsBaseUrl:
        'https://api.unifaun.com/rs-extapi/v1/delivery-checkouts-widget/logos',
    });

    unifaunWidget.value.enable();
    unifaunWidget.value.updateList(res);
    unifaunInitiated.value = true;
    deliveryLoading.value = false;
    setPayment();

  } else {
    console.error('Could not iniate unifaun');
  }
};

const clearPostalNumber = () => {
  unifaunInitiated.value = false;
};

const onSelectShippingMethod = async(setDelivery: UnifaunSetDelivery) => {
  unifaunWidgetValid.value = true;

  const shippingMethod = config.shippingMethods?.find(
    (method) => method.unifaun_shipping_method === setDelivery.selectedOptionId
  );
  if (shippingMethod) {
    suspendKco();
    const oldShipping = selectedShipping.value;
    selectedShipping.value = shippingMethod.centra_shipping_method;
    isLoadingPrice.value = true;
    additionalNotes(
      {
        selectedOptionId: setDelivery.selectedOptionId,
        selectedAddons: setDelivery.selectedAddons,
        selectedAgent: setDelivery.agent,
      },
      shippingMethod.unifaun_shipping_ids
    );

    const res = ref('');
    
    res.value = await centraPut(`/shipping-methods/${shippingMethod.centra_shipping_method.trim()}`);

    if (res.value) {
      cartStore._cartData = res;
    } else {
      uiStore.setError('Couldn\'t update shipping method');
      selectedShipping.value = oldShipping;
    }
    isLoadingPrice.value = false;
  } else {
    uiStore.setError('Couldn\'t find shipping method');
  }
};

const updateDeliveryOptions = async() => {
  if (unifaunWidget.value) {
    unifaunWidget.value.disable();
    const res = await unifaunGet(unifaunWidgetUrl.value);
    if (res) {
      unifaunWidget.value.updateList(res);
    }
    unifaunWidget.value.enable();
  }
};

watch(()=> cartStore.cartData.totalCostAsNumber, () => {
  updateDeliveryOptions();
});

// * * * * * * * * * * * * * * * * * * * HELP MENU
const openHelpMenu = (isHelp: boolean) => {
  if (isHelp) {
    informationTitle.value = config.checkout?.title ?? '';
    informationContent.value = config.checkout?.text ?? '';
  } else {
    informationTitle.value = $lang('checkout', 'ullmax_foundation');
    informationContent.value = checkoutPageDocument.value.charityFlyoutText || '';
  }
  helpMenuOpen.value = true;
};
const closeHelpMenu = () => {
  informationTitle.value = '';
  informationContent.value = '';
  helpMenuOpen.value = false;
};

// * * * * * * * * * * * * * * * * * * * VOUCHERS
const voucherApplied = () => {
  resumeKco();
  voucherMenuIsToggled.value = false;
};

const deleteVoucher = async(voucher: string) => {
  // const res = await centraApi.delete(`/vouchers/${voucher}`);
  suspendKco();
  const res = await centraDelete<CartType>(`/vouchers/${voucher}`);
  if (res) {
    cartStore._cartData = res;
  }
  resumeKco();
};

// * * * * * * * * * * * * * * * * * * * NEWSLETTER
watch(newsletterChecked, async() => {
  sessionStorage.setItem('acceptedNewsletter', newsletterChecked.value ? 'true':'false');
  suspendKco();
  await cartStore.updatePaymentFields({
    address: {
      newsletter: newsletterChecked.value,
    },
  });
  resumeKco();
});

const uuidv4 = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
    /[xy]/g,
    function(c) {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    }
  );
};

// * * * * * * * * * * * * * * * * * * * UPDATE SETTINGS

/**
 * Sets additional data as a string
 * PrepareId | CustNo (club.custNo) | Unifaun |  Seller.WebshopsToken | Club.RoundID
 */
const additionalNotes = (
  shippingOptions: ShippingOptionsInterface | null = null,
  shippingIds: string | null = null
) => {
  /*console.group();
  console.log(clubCookie.value);
  console.log(shippingOptions);
  console.log(shippingIds);
  console.groupEnd();*/

  const stored = orderAdditionalData.value ? orderAdditionalData.value : {} as OrderAdditionalDataInterface;

  if (!stored.preparedId) {
    stored.preparedId = uuidv4();
  }
  if (clubCookie.value) {
    if (clubCookie.value.CustNo) {
      stored.custNo = clubCookie.value.CustNo;
    }
    if (clubCookie.value.RoundID) {
      stored.clubRoundId = clubCookie.value.RoundID;
    } else {
      console.log('No?');
    }
  }
  if (shippingOptions) {
    stored.shippingOptions = shippingOptions;
  }
  if (shippingIds) {
    stored.shippingIds = shippingIds;
  }
  if (sellerCookie.value) {
    if (sellerCookie.value.WebshopToken) {
      stored.sellerWebshopToken = sellerCookie.value.WebshopToken;
    }
    if (sellerCookie.value.WebshopID) {
      stored.sellerWebshopID = sellerCookie.value.WebshopID;
    }
  }

  orderAdditionalData.value = stored;
  const notes = `${stored.preparedId}|${stored.custNo}|${stored.shippingIds}|${stored.sellerWebshopToken}|${stored.clubRoundId}|${stored.sellerWebshopID}`;

  updateAdditionalFields(notes);

  console.groupEnd();
  return notes;
};

const updateAdditionalFields = async(notes: string) => {
  await cartStore.updatePaymentFields({
    additionalNotes: notes,
  });
};

// * * * * * * * * * * * * * * * * * * * PAYMENT (klarna)
const setPayment = async() => {
  const userEmail = (cartStore.cartData.user && cartStore.cartData.user.email) || '';
  const body = {
    paymentMethod:
        cartStore.cartData.paymentMethods[0]?.paymentMethod ?? 'kco',
    paymentReturnPage: `${runtimeConfig.public.frontendUrl}/${config.market.country.toLowerCase()}/payment-return`.replace('http://', 'https://'),
    paymentFailedPage: `${runtimeConfig.public.frontendUrl}${route.path}`.replace('http://', 'https://'),
    termsAndConditions: true,
    address: {
      country: config.market.country,
      newsletter: newsletterChecked.value,
      email: userEmail,
    },
    additionalNotes: additionalNotes(),
  };
  const res = await centraPost<CentraPaymentResponse>('/payment', body);

  if (res) {
    injectKco(res.formHtml);
  } else {
    console.error();
  }
};

const el = ref<HTMLDivElement | null>(null);

const injectKco = (snippet: string) => {
  if (el.value) {
    el.value.innerHTML = snippet;
    const scriptsTags = el.value.getElementsByTagName('script');

    for (let i = 0; i < scriptsTags.length; i++) {
      const parentNode = scriptsTags[i].parentNode;
      const newScriptTag = document.createElement('script');
      newScriptTag.type = 'text/javascript';
      newScriptTag.text = scriptsTags[i].text;
      parentNode!.removeChild(scriptsTags[i]);
      parentNode!.appendChild(newScriptTag);
    }
  }
};

const suspendKco = () => {
  if (window._klarnaCheckout) {
    window._klarnaCheckout(function(api: { suspend: () => void }) {
      api.suspend();
    });
  }
};
const resumeKco = () => {
  if (window._klarnaCheckout) {
    window._klarnaCheckout(function(api: { resume: () => void }) {
      api.resume();
    });
  }
};

// * * * * * * * * * * * * * * * * * * * CROSS SELL
const getCrossSellProducts = async() => {
  if (checkoutPageDocument.value.crossSell.products.length > 0 && !cartStore.cartData.isGiftCardCheckout) {
    const filterItems = cartStore.cartData.items.map((product: CartItemType) => {
      const thisProduct = new Product({ ecomData: product.product, contentData: null });
      return thisProduct.ecomId;
    });
    cartStore.cartData.items.forEach((product: CartItemType) => {
      filterItems.push(
        new Product({ ecomData: product.product, contentData: null }).ecomId
      );
    });
    if (config.cart_cross_sell?.products.length > 0) {
      const { products } = await fetchProducts({
        products: config.cart_cross_sell.products,
      });

      crossSellProducts.value = products
        .map(
          (product: EcomProduct) =>
            new Product({ ecomData: product, contentData: null })
        )
        .filter((product: Product) => !filterItems.includes(product.ecomId))
        .slice(0, 3);
    }
  }
};

// * * * * * * * * * * * * * * * * * * * Initial tracking
const sendGtmCheckoutStep = () => {
  const sendItems = items.value.map((item: CartItem) => {
    return {
      id: item.sku,
      feedId: item.sku,
      ...item.product.gtmProductValues,
      quantity: item.quantity,
    };
  });
  pushToDataLayer({
    event: 'checkoutStep',
    ecommerce: {
      currencyCode: cartStore.cartData.currencyCode,
      checkout: {
        actionField: {
          step: '1',
        },
        products: sendItems,
      },
    },
  });
};

</script>

<style scoped lang="postcss">
.section {
  @apply bg-white pb-32 mb-24;
}
@screen md {
  .section {
    @apply pb-24 mb-24 rounded-xl;
  }
}

.checkout {
  max-width: 1046px;
  @apply mx-auto;
}
</style>
